import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import recordsService from '../../services/recordsService';
import FieldRow from './FieldRow';
import formatFieldValue, { EMPTY_VALUE } from './formatFieldValue';
import SECTION_TITLES from './sectionTitles';
import VehicleDiagram from './serviceMap/VehicleDiagram';
import { STATUS } from './serviceMap/zoneStyles';

// The parts a visit's lines were for, in line order, each once.
const zoneLabels = (record) => [...new Set((record.details || []).map((detail) => detail.zone_label).filter(Boolean))];

/**
 * One record, every configured field. A transaction also shows its line items when the company allows it — the
 * server only sends `details` in that case, so their presence is the switch. With the vehicle service map on, the
 * server also sends `service_map` (only alongside `details`) and each line's part.
 *
 * @param {string|null} recordId - open when set
 * @param {object} section - this section's config from group.portal_records.sections
 */
const RecordDetailModal = ({ groupId, sectionKey, section, recordId, onClose }) => {
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  // On a phone the dialog takes the whole screen and line items stack. WHY: as a floating box with a 3-4 column
  // table, a 375px screen squeezed item names to one word per line and clipped the last column off the side.
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));

  useEffect(() => {
    if (!recordId) return;
    let cancelled = false;
    setRecord(null);
    setError(null);
    recordsService
      .getRecord(groupId, sectionKey, recordId)
      .then((result) => {
        if (!cancelled) setRecord(result);
      })
      .catch(() => {
        if (!cancelled) setError('This record could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, sectionKey, recordId]);

  const fields = section?.fields || [];
  const detailFields = section?.detail_fields || [];

  return (
    <Dialog open={Boolean(recordId)} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isPhone}>
      <DialogTitle>{SECTION_TITLES[sectionKey]}</DialogTitle>
      <DialogContent dividers>
        {!record && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        {record && (
          <>
            {fields.map((field) => (
              <FieldRow key={field.id} label={field.name} value={record[field.id]} type={field.type} fieldId={field.id} />
            ))}

            {record.service_map?.zones?.length > 0 && (
              // With the company's vehicle service map on: what this visit worked on, as a small map.
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', gap: 2.25, alignItems: 'center', flexWrap: 'wrap' }}>
                  <VehicleDiagram
                    width={64}
                    statuses={Object.fromEntries(record.service_map.zones.map((zone) => [zone, 'serviced']))}
                    label={`Worked on this visit: ${zoneLabels(record).join(', ')}`}
                  />
                  <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', mb: 1 }}>WORKED ON THIS VISIT</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      {zoneLabels(record).map((zoneLabel) => (
                        <Box
                          component="span"
                          key={zoneLabel}
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#fff',
                            bgcolor: STATUS.serviced.fill,
                            borderRadius: '14px',
                            px: 1.5,
                            py: 0.6,
                          }}
                        >
                          {zoneLabel}
                        </Box>
                      ))}
                    </Box>
                    {(record.service_map.vehicle || record.service_map.mileage) && (
                      <Typography variant="body2" color="text.secondary">
                        {[
                          record.service_map.vehicle,
                          record.service_map.mileage && `${Number(record.service_map.mileage).toLocaleString()} mi`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}

            {Array.isArray(record.details) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  What&apos;s included
                </Typography>
                {record.details.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No line items on this visit.
                  </Typography>
                ) : isPhone ? (
                  // Each line: its first column as the name, the rest beneath it, then the part it was for.
                  <Stack divider={<Divider flexItem />} spacing={1.25}>
                    {record.details.map((detail) => {
                      const [nameField, ...otherFields] = detailFields;
                      return (
                        <Box key={detail.id}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                            {nameField ? formatFieldValue(detail[nameField.id], nameField.type, nameField.id) : EMPTY_VALUE}
                          </Typography>
                          {otherFields.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {otherFields
                                .map((field) => `${field.name}: ${formatFieldValue(detail[field.id], field.type, field.id)}`)
                                .join(' · ')}
                            </Typography>
                          )}
                          {record.service_map && detail.zone_label && (
                            <Typography variant="body2" sx={{ color: STATUS.serviced.fill, fontWeight: 600 }}>
                              {detail.zone_label}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {detailFields.map((field) => (
                          <TableCell key={field.id} sx={{ fontWeight: 600 }}>
                            {field.name}
                          </TableCell>
                        ))}
                        {record.service_map && <TableCell sx={{ fontWeight: 600 }}>Part</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {record.details.map((detail) => (
                        <TableRow key={detail.id}>
                          {detailFields.map((field) => (
                            <TableCell key={field.id}>{formatFieldValue(detail[field.id], field.type, field.id)}</TableCell>
                          ))}
                          {record.service_map && (
                            <TableCell
                              sx={{
                                color: detail.zone_label ? STATUS.serviced.fill : 'text.secondary',
                                fontWeight: detail.zone_label ? 600 : 400,
                              }}
                            >
                              {detail.zone_label || EMPTY_VALUE}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordDetailModal;
